import React, {
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { cn, themeTextStyles, normalAnimationClass } from "../../styles";
import { ThemeColor, BaseElementProps, RenderProp } from "../../props";
import { Typography, TypographyContent } from "../../atoms";

// ─── Context ─────────────────────────────────────────────────────────────────

interface StepperContextType {
  activeStep: number;
  color: ThemeColor;
  dense: boolean;
}

const StepperContext = createContext<StepperContextType>({
  activeStep: -1,
  color: ThemeColor.primary,
  dense: false,
});

// ─── Color maps ──────────────────────────────────────────────────────────────

const activeColorStyles: Record<ThemeColor, string> = {
  primary:     "bg-primary-500 text-front-50 border-primary-500 dark:bg-primary-600 dark:border-primary-600",
  secondary:   "bg-secondary-500 text-front-50 border-secondary-500 dark:bg-secondary-600 dark:border-secondary-600",
  gray:        "bg-back-400 text-front-50 border-back-400 dark:bg-back-600 dark:border-back-600",
  error:       "bg-error-600 text-front-50 border-error-600 dark:bg-error-700 dark:border-error-700",
  warning:     "bg-warning-600 text-front-50 border-warning-600 dark:bg-warning-700 dark:border-warning-700",
  success:     "bg-success-600 text-front-50 border-success-600 dark:bg-success-700 dark:border-success-700",
  transparent: "bg-front-200/20 text-front-800 border-front-200/40 dark:bg-front-700/20 dark:text-front-100",
  none: "", danger: "", alert: "", done: "",
};
activeColorStyles.danger = activeColorStyles.error;
activeColorStyles.alert  = activeColorStyles.warning;
activeColorStyles.done   = activeColorStyles.success;

const completedColorStyles: Record<ThemeColor, string> = {
  primary:     "border-primary-500 text-primary-500 dark:border-primary-500 dark:text-primary-400",
  secondary:   "border-secondary-500 text-secondary-500 dark:border-secondary-500 dark:text-secondary-400",
  gray:        "border-back-400 text-back-400 dark:border-back-600 dark:text-back-500",
  error:       "border-error-600 text-error-600 dark:border-error-600 dark:text-error-500",
  warning:     "border-warning-600 text-warning-600 dark:border-warning-600 dark:text-warning-500",
  success:     "border-success-600 text-success-600 dark:border-success-600 dark:text-success-500",
  transparent: "border-front-300 text-front-500 dark:border-front-600 dark:text-front-400",
  none: "", danger: "", alert: "", done: "",
};
completedColorStyles.danger = completedColorStyles.error;
completedColorStyles.alert  = completedColorStyles.warning;
completedColorStyles.done   = completedColorStyles.success;

const connectorColorStyles: Record<ThemeColor, string> = {
  primary:     "bg-primary-500 dark:bg-primary-500",
  secondary:   "bg-secondary-500 dark:bg-secondary-500",
  gray:        "bg-back-400 dark:bg-back-600",
  error:       "bg-error-600 dark:bg-error-600",
  warning:     "bg-warning-600 dark:bg-warning-600",
  success:     "bg-success-600 dark:bg-success-600",
  transparent: "bg-front-300 dark:bg-front-600",
  none: "", danger: "", alert: "", done: "",
};
connectorColorStyles.danger = connectorColorStyles.error;
connectorColorStyles.alert  = connectorColorStyles.warning;
connectorColorStyles.done   = connectorColorStyles.success;

// ─── Step props (declared early so ParallelStep can share the same type) ──────

/** @internal injected by Stepper parent via cloneElement */
interface StepperStepInjectedProps {
  _index?: number;
  _isLast?: boolean;
  _maxStackOffset?: number;
}

export interface StepperStepProps extends StepperStepInjectedProps {
  /** Title displayed below the step indicator. */
  title: TypographyContent;

  /** Optional secondary text displayed below the title. */
  subtitle?: TypographyContent;

  /** Forces this step active regardless of `Stepper.activeStep`. */
  active?: boolean;

  /** Forces this step completed regardless of index position. */
  completed?: boolean;

  /** Overrides the Stepper-level color for this step's bubble, label, and connector. */
  color?: ThemeColor;

  /**
   * Render prop for the step bubble symbol.
   * Caller is responsible for icons, localization scopes, etc.
   * When omitted the step number is shown.
   */
  renderSymbol?: RenderProp;

  /**
   * Makes the step clickable. Wraps the step in a button element.
   * Use for non-linear wizard navigation.
   */
  onClick?: () => void;

  /**
   * Prevents clicking. Only applies when `onClick` is provided.
   * Renders the step at reduced opacity with a not-allowed cursor.
   */
  disabled?: boolean;

  /**
   * `Stepper.ParallelStep` children render as a stacked bubble with a dropdown
   * listing each parallel path and its state.
   */
  children?: React.ReactNode;
}

/** Same props as `Stepper.Step` — only the rendering differs (dropdown row vs full step). */
export type StepperParallelStepProps = StepperStepProps;

// ─── StepIconBubble ───────────────────────────────────────────────────────────

interface StepIconProps {
  index: number;
  active: boolean;
  completed: boolean;
  color: ThemeColor;
  dense: boolean;
  renderSymbol?: RenderProp;
}

function StepIconBubble({ index, active, completed, color, dense, renderSymbol }: StepIconProps) {
  const sizeClass     = dense ? "w-6 h-6 text-xs" : "w-8 h-8 text-sm";
  const inactiveClass = "border-2 border-back-300 text-front-400 bg-transparent dark:border-back-600 dark:text-front-500";
  const colorClass    = active ? activeColorStyles[color] : completed ? completedColorStyles[color] : inactiveClass;

  return (
    <div
      className={cn(
        "rounded-full flex items-center justify-center font-semibold border-2 shrink-0",
        normalAnimationClass,
        sizeClass,
        colorClass
      )}
    >
      {renderSymbol
        ? renderSymbol({ sizeClassName: dense ? "w-3.5 h-3.5" : "w-4 h-4", color: ThemeColor.none, dense, muted: false, disabled: false, loading: false })
        : index + 1}
    </div>
  );
}

// ─── StackedBubbleWrapper ─────────────────────────────────────────────────────
// Shadow circles are rendered BEFORE the main bubble in DOM order so they sit
// visually behind it without needing z-index. The button wrapper gives each
// shadow its own position in the padded area above the main bubble.

interface StackedWrapperProps {
  count: number;
  dense: boolean;
  open: boolean;
  onClick: () => void;
  children: React.ReactNode;
}

function StackedBubbleWrapper({ count, dense, open, onClick, children }: StackedWrapperProps) {
  const sizeClass  = dense ? "w-6 h-6" : "w-8 h-8";
  const stackPad   = count >= 3 ? 12 : 6;
  const shadow1Top = count >= 3 ? "top-[6px]" : "top-0";

  return (
    <button
      type="button"
      aria-haspopup="listbox"
      aria-expanded={open}
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      className={cn(
        "relative cursor-pointer rounded-full outline-none",
        "focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-offset-1"
      )}
      style={{ paddingTop: stackPad }}
    >
      {/* Back shadow — rendered first = sits behind everything */}
      {count >= 3 && (
        <div className={cn(
          "absolute left-1/2 -translate-x-1/2 top-0 rounded-full border-2",
          "border-back-200 bg-back-50 dark:border-back-700 dark:bg-back-950 opacity-50",
          sizeClass
        )} />
      )}
      {/* Middle shadow */}
      <div className={cn(
        "absolute left-1/2 -translate-x-1/2 rounded-full border-2",
        "border-back-300 bg-back-100 dark:border-back-600 dark:bg-back-900 opacity-80",
        sizeClass,
        shadow1Top
      )} />
      {/* Main bubble — rendered last = naturally on top */}
      <div className={cn(
        "relative rounded-full transition-shadow",
        normalAnimationClass,
        open && "ring-2 ring-primary-400 ring-offset-1 dark:ring-offset-back-900"
      )}>
        {children}
      </div>
    </button>
  );
}

// ─── ParallelDropdown ─────────────────────────────────────────────────────────

interface ParallelDropdownProps {
  steps: StepperParallelStepProps[];
  color: ThemeColor;
  dense: boolean;
}

function ParallelDropdown({ steps, color, dense }: ParallelDropdownProps) {
  const symbolSize  = dense ? "w-3 h-3" : "w-[15px] h-[15px]";
  const anyHasSymbol = steps.some((s) => s.renderSymbol !== undefined);

  return (
    <div
      role="listbox"
      className={cn(
        "absolute top-full left-1/2 -translate-x-1/2 mt-2 z-50",
        "bg-white dark:bg-back-900",
        "border border-back-200 dark:border-back-700",
        "rounded-xl shadow-lg overflow-hidden",
        "min-w-44"
      )}
    >
      {/* Caret arrow */}
      <div className="absolute -top-[5px] left-1/2 -translate-x-1/2 w-2.5 h-2.5 bg-white dark:bg-back-900 border-l border-t border-back-200 dark:border-back-700 rotate-45" />

      <div className={cn("flex flex-col gap-px", dense ? "p-1" : "p-1.5")}>
        {steps.map((step, i) => {
          const isActive    = !!step.active;
          const isCompleted = !!step.completed;

          const rowTextClass = isActive
            ? themeTextStyles[color]
            : isCompleted
              ? cn(themeTextStyles[color], "opacity-60")
              : "text-front-500 dark:text-front-400";

          return (
            <div
              key={i}
              role="option"
              aria-selected={isActive}
              className={cn(
                "flex items-center rounded-lg",
                dense ? "gap-1.5 px-2 py-1" : "gap-2 px-2.5 py-1.5",
                isActive && "bg-primary-50 dark:bg-primary-900/20",
              )}
            >
              {anyHasSymbol && (
                step.renderSymbol
                  ? <span className={rowTextClass}>{step.renderSymbol({ sizeClassName: symbolSize, color: ThemeColor.none, dense, muted: false, disabled: false, loading: false })}</span>
                  : <span style={{ display: "inline-block", flexShrink: 0 }} className={cn(symbolSize)} />
              )}

              <div className="flex-1 flex flex-col min-w-0">
                <Typography.Span
                  className={cn("text-xs leading-tight", rowTextClass)}
                  content={step.title}
                />
                {step.subtitle && (
                  <Typography.Span
                    className={cn("text-xs leading-tight opacity-60", rowTextClass)}
                    content={step.subtitle}
                  />
                )}
              </div>

              {/* State dot */}
              {isActive && (
                <span className={cn(
                  "shrink-0 rounded-full animate-pulse",
                  dense ? "w-1.5 h-1.5" : "w-2 h-2",
                  connectorColorStyles[color]
                )} />
              )}
              {isCompleted && !isActive && (
                <span className={cn(
                  "shrink-0 rounded-full opacity-50",
                  dense ? "w-1.5 h-1.5" : "w-2 h-2",
                  connectorColorStyles[color]
                )} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Stepper.ParallelStep ─────────────────────────────────────────────────────

/**
 * Data-carrier child of `Stepper.Step` for JSX-based parallel step composition.
 * Takes exactly the same props as `Stepper.Step` — only the rendering differs.
 * Renders nothing — `Stepper.Step` reads its props to populate the dropdown.
 *
 * @example
 * <Stepper.Step title="Deploy">
 *   <Stepper.ParallelStep title="Region EU" renderSymbol={...} active />
 *   <Stepper.ParallelStep title="Region US" renderSymbol={...} completed />
 * </Stepper.Step>
 */
const StepperParallelStep = (_props: StepperParallelStepProps) => null;
StepperParallelStep.displayName = "Stepper.ParallelStep";

// ─── Stepper.Step ─────────────────────────────────────────────────────────────

const StepperStep = ({
  title,
  subtitle,
  renderSymbol,
  active,
  completed,
  color: colorProp,
  onClick,
  disabled,
  children,
  _index = 0,
  _isLast = false,
  _maxStackOffset = 0,
}: StepperStepProps) => {
  const { activeStep, color: contextColor, dense } = useContext(StepperContext);
  const color = colorProp ?? contextColor;
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const childParallelSteps = React.Children.toArray(children)
    .filter(
      (child) =>
        React.isValidElement(child) &&
        (child.type === StepperParallelStep ||
          (child.type as any)?.displayName === "Stepper.ParallelStep")
    )
    .map((child) => (child as React.ReactElement<StepperParallelStepProps>).props);

  const allParallelSteps = childParallelSteps;

  const hasParallel   = allParallelSteps.length > 0;
  const parallelCount = allParallelSteps.length;

  const isActive    = active    ?? (_index === activeStep);
  const isCompleted = completed ?? (_index < activeStep);

  // All bubble centers must sit at the same y so connector lines stay aligned.
  // stackOffset is this step's own shadow padding; _maxStackOffset is the tallest
  // across all sibling steps. Extra padding bridges the difference for shorter steps.
  const stackOffset    = hasParallel ? (parallelCount >= 3 ? 12 : 6) : 0;
  const iconAreaPadding = _maxStackOffset - stackOffset;
  const bubbleCenterPx = (dense ? 12 : 16) + _maxStackOffset;

  const labelColorClass = isActive
    ? themeTextStyles[color]
    : isCompleted
      ? cn(themeTextStyles[color], "opacity-70")
      : "text-front-400 dark:text-front-500";

  const closeDropdown = useCallback(() => setDropdownOpen(false), []);

  useEffect(() => {
    if (!dropdownOpen) return;
    const onMouse = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) closeDropdown();
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") closeDropdown(); };
    document.addEventListener("mousedown", onMouse);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onMouse);
      document.removeEventListener("keydown", onKey);
    };
  }, [dropdownOpen, closeDropdown]);

  const iconBubble = (
    <StepIconBubble
      index={_index}
      active={isActive}
      completed={isCompleted}
      renderSymbol={renderSymbol}
      color={color}
      dense={dense}
    />
  );

  const baseClass = cn("flex flex-col items-center shrink-0 relative", dense ? "gap-1" : "gap-1.5");
  const clickableClass = cn(
    baseClass,
    "cursor-pointer select-none transition-opacity duration-150",
    "outline-none focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-offset-1 rounded-lg",
    "hover:opacity-80 active:opacity-60",
    "disabled:opacity-40 disabled:cursor-not-allowed"
  );

  const stepContent = (
    <>
      <div style={iconAreaPadding > 0 ? { paddingTop: iconAreaPadding } : undefined}>
        {hasParallel ? (
          <StackedBubbleWrapper
            count={parallelCount}
            dense={dense}
            open={dropdownOpen}
            onClick={() => setDropdownOpen((v) => !v)}
          >
            {iconBubble}
          </StackedBubbleWrapper>
        ) : (
          iconBubble
        )}
      </div>

      <div className="flex flex-col items-center">
        <Typography.Span
          className={cn(
            "text-xs text-center leading-tight transition-colors",
            normalAnimationClass,
            labelColorClass
          )}
          content={title}
        />
        {subtitle && (
          <Typography.Span
            className={cn(
              "text-xs text-center leading-tight opacity-60 transition-colors",
              normalAnimationClass,
              labelColorClass
            )}
            content={subtitle}
          />
        )}
      </div>

      {hasParallel && dropdownOpen && (
        <ParallelDropdown steps={allParallelSteps} color={color} dense={dense} />
      )}
    </>
  );

  return (
    <React.Fragment>
      {onClick ? (
        <button
          type="button"
          ref={containerRef as unknown as React.Ref<HTMLButtonElement>}
          disabled={disabled}
          onClick={onClick}
          className={clickableClass}
        >
          {stepContent}
        </button>
      ) : (
        <div ref={containerRef} className={baseClass}>
          {stepContent}
        </div>
      )}

      {!_isLast && (
        <div className="flex-1 flex items-start px-1" style={{ paddingTop: bubbleCenterPx }}>
          <div className={cn(
            "h-0.5 w-full transition-colors",
            normalAnimationClass,
            isCompleted ? connectorColorStyles[color] : isActive ? cn(connectorColorStyles[color], "opacity-50") : "bg-back-200 dark:bg-back-700"
          )} />
        </div>
      )}
    </React.Fragment>
  );
};

StepperStep.displayName = "Stepper.Step";

// ─── Stepper ──────────────────────────────────────────────────────────────────

export interface StepperProps extends BaseElementProps {
  /** `Stepper.Step` elements. */
  children: React.ReactNode;

  /**
   * Zero-based index of the active step.
   * Overridden by `active` prop on individual `Stepper.Step` elements.
   * @default -1
   */
  activeStep?: number;

  /**
   * Color applied to active/completed indicators and connectors.
   * @default ThemeColor.primary
   */
  color?: ThemeColor;
}

/**
 * Horizontal stepper for displaying multi-step progress.
 * Compose with `Stepper.Step` sub-components.
 *
 * Steps with `Stepper.ParallelStep` children render as a stacked bubble. Clicking
 * opens a dropdown listing each parallel path and its current state.
 *
 * @example
 * <Stepper activeStep={1}>
 *   <Stepper.Step title="Start" iconPath={mdiPlay} />
 *   <Stepper.Step title="Processing">
 *     <Stepper.ParallelStep title="Migration" active />
 *     <Stepper.ParallelStep title="Validation" completed />
 *   </Stepper.Step>
 *   <Stepper.Step title="Done" />
 * </Stepper>
 */
const StepperBase = forwardRef<HTMLDivElement, StepperProps>(
  ({ children, activeStep = -1, color = ThemeColor.primary, dense = false, className }, ref) => {
    const steps = React.Children.toArray(children).filter(
      (child) =>
        React.isValidElement(child) &&
        (child.type === StepperStep ||
          (child.type as any)?.displayName === "Stepper.Step")
    ) as React.ReactElement<StepperStepInjectedProps>[];

    const resolvedActive = steps.findIndex(
      (s) => React.isValidElement(s) && (s.props as StepperStepProps).active
    );
    const effectiveActive = resolvedActive >= 0 ? resolvedActive : activeStep;

    const maxStackOffset = steps.reduce((max, step) => {
      const parallelChildren = React.Children.toArray((step.props as StepperStepProps).children ?? []).filter(
        (child) =>
          React.isValidElement(child) &&
          (child.type === StepperParallelStep ||
            (child.type as any)?.displayName === "Stepper.ParallelStep")
      );
      const count = parallelChildren.length;
      return Math.max(max, count >= 3 ? 12 : count > 0 ? 6 : 0);
    }, 0);

    return (
      <StepperContext.Provider value={{ activeStep: effectiveActive, color, dense }}>
        <div ref={ref} className={cn("flex items-start w-full", className)}>
          {steps.map((step, i) =>
            React.cloneElement(step, { _index: i, _isLast: i === steps.length - 1, _maxStackOffset: maxStackOffset })
          )}
        </div>
      </StepperContext.Provider>
    );
  }
);

StepperBase.displayName = "Stepper";

export const Stepper = Object.assign(StepperBase, {
  Step: StepperStep,
  ParallelStep: StepperParallelStep,
});
